import { chakra } from '@chakra-ui/react';
import React from 'react';

import type { NavItem } from 'types/client/navigation';

import { route } from 'nextjs-routes';

import { isInternalItem } from 'lib/hooks/useNavItems';
import { Link } from 'toolkit/chakra/link';

import LightningLabel from '../LightningLabel';
import NavLinkIcon from '../NavLinkIcon';
import { checkRouteHighlight } from '../utils';

interface Props {
  className?: string;
  item: NavItem;
  noIcon?: boolean;
  compact?: boolean;
}

const NavLink = ({ className, item, noIcon, compact }: Props) => {
  const isInternalLink = isInternalItem(item);

  const isActive = 'isActive' in item && item.isActive;

  const isHighlighted = checkRouteHighlight(item);

  return (
    <chakra.li
      listStyleType="none"
    >
      <Link
        className={ className }
        href={ isInternalLink ? route(item.nextRoute) : item.url }
        external={ !isInternalLink }
        display="flex"
        alignItems="center"
        variant="navigation"
        { ...(isActive ? { 'data-selected': true } : {}) }
        aria-current={ isActive ? 'page' : undefined }
        w={ compact ? 'auto' : '224px' }
        minH={ compact ? 10 : undefined }
        px={ compact ? 4 : 2 }
        py={ compact ? 2.5 : '9px' }
        textStyle="sm"
        fontWeight={ compact ? 600 : 500 }
        borderRadius={ compact ? 'lg' : 'base' }
        color={ compact && isActive ? 'blue.700' : undefined }
        bgColor={ compact && isActive ? 'blue.50' : undefined }
        transitionProperty="background-color, color, box-shadow"
        transitionDuration="fast"
        _hover={ compact ? {
          color: 'blue.800',
          bgColor: isActive ? 'blue.50' : 'orange.50',
          textDecoration: 'none',
        } : undefined }
        _focusVisible={ compact ? {
          outline: '2px solid',
          outlineColor: 'blue.500',
          outlineOffset: '2px',
        } : undefined }
      >
        { !noIcon && <NavLinkIcon item={ item } mr={ 3 }/> }
        <chakra.span>{ item.text }</chakra.span>
        { isHighlighted && (
          <LightningLabel
            iconColor={ isActive ? 'link.navigation.bg.selected' : 'link.navigation.bg.group' }
            position={{ lg: 'static' }}
            ml={{ lg: '2px' }}
            isCollapsed={ false }
          />
        ) }
      </Link>
    </chakra.li>
  );
};

export default React.memo(chakra(NavLink));
